import { useQueryClient, type QueryKey } from "@tanstack/react-query";

/**
 * A retry for a query that is paused offline. `refetch()` cannot serve here:
 * a paused query that has never succeeded answers it by returning the paused
 * retryer's existing promise — `continueRetry()` only clears a cancellation
 * flag, and a paused retryer resumes solely when the online signal comes
 * back. The click would fire nothing, precisely in the one state whose
 * surface offers the control (#215).
 *
 * Cancelling first settles that retryer and returns the query to `idle`, so
 * the refetch starts a fresh attempt, which `networkMode: "offlineFirst"`
 * lets fire regardless of what the browser believes about connectivity. The
 * browser's offline signal can lag a connection that has already returned —
 * that is why the control exists — and if the connection is genuinely still
 * gone, the new attempt fails, its retries pause, and the surface returns to
 * the offline state it started from: one bounded attempt per click, no loop.
 *
 * Scoped to one query key on purpose. Flipping `onlineManager` back online
 * would also resume this query, but it would resume every other paused query
 * and mutation with it, and it would swallow the real reconnect transition
 * that `refetchOnReconnect` self-healing listens for.
 */
export function useRetryPausedQuery(queryKey: QueryKey): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient
      .cancelQueries({ queryKey })
      .then(() => queryClient.refetchQueries({ queryKey }));
  };
}

/**
 * The same recovery over a SET of keys whose membership is data rather than
 * code.
 *
 * A surface whose subject is one resource names its key literally and calls the
 * hook above. A surface whose subject is a set — the adopted items supplying a
 * clinical section (#443), one version-history read each — cannot: the ids come
 * from a response, so the number of keys changes between renders, and calling
 * the single-key hook once per id would be a conditional hook count.
 *
 * Still one key per `cancelQueries`/`refetchQueries` pair, never a prefix. A
 * prefix over the library root would resume every paused library query in the
 * cache, including reads no surface on screen is waiting for, and it would
 * swallow the reconnect transition `refetchOnReconnect` listens for — the same
 * reason the single-key form is scoped the way it is.
 */
export function useRetryPausedQueries(
  queryKeys: readonly QueryKey[],
): () => void {
  const queryClient = useQueryClient();
  return () => {
    for (const queryKey of queryKeys) {
      void queryClient
        .cancelQueries({ queryKey })
        .then(() => queryClient.refetchQueries({ queryKey }));
    }
  };
}

/**
 * The same recovery again, with the key supplied at the CLICK rather than at
 * the hook call.
 *
 * For a surface whose controls are per ROW: a Project's adoptions render one
 * row per pin, and a row whose pinned version failed to read offers a retry
 * that names that row — "Read PHQ-9 at v2 again" (#369). Neither form above
 * fits. The single-key form cannot be called once per row, because the number
 * of rows is data and a per-row hook call would be a changing hook count; and
 * the set form returns ONE function over every failed key, so a button sitting
 * in one row would silently re-read the others as well, which is precisely the
 * per-asset/global confusion #443's review round removed from the block beside
 * it.
 *
 * One hook, one `queryClient`, and the same cancel-then-refetch a paused query
 * needs (#215) — never a prefix, for the reason the two above are scoped the
 * way they are.
 */
export function useRetryPausedQueryByKey(): (queryKey: QueryKey) => void {
  const queryClient = useQueryClient();
  return (queryKey: QueryKey) => {
    void queryClient
      .cancelQueries({ queryKey })
      .then(() => queryClient.refetchQueries({ queryKey }));
  };
}
